import { useGetRescheduleOptions } from "hooks/api/appointments/useGetRescheduleOptions";
import React, { createContext, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { STEPS } from "./utils";

type ReschedulingContextType = {
  currentStep: string;
  setStep: (step: any) => void;
  onBack: () => void;
  rescheduleOptionsLoading: boolean;
  callInfo: any;
  clinicianAvailableAppointmentTimes: any;
  intersectionAvailability: any;
  reschedulingHistory: any[];
  newAppointmentTime: any;
  setNewAppointmentTime: (time: any) => void;
  clientMakeupDays: any;
  rescheduling12hrWindowPilot: boolean;
};

const VALID_STEPS = new Set(Object.values(STEPS));

const ReschedulingContext = createContext<ReschedulingContextType | undefined>(undefined);

export const ReschedulingProvider = ({
  children,
  rescheduling12hrWindowPilot,
}: {
  children: React.ReactNode;
  rescheduling12hrWindowPilot: boolean;
}) => {
  const { id: appointmentId, step: urlStep } = useParams<{ id: string; step?: string }>();
  const history = useHistory();
  const searchParams = history.location.search;
  const getInitialHistory = () => {
    if (!urlStep || !VALID_STEPS.has(urlStep) || urlStep === STEPS.makeupDay || urlStep === STEPS.recommendations) {
      return [];
    }
    const backMap: Record<string, string[]> = {
      [STEPS.customDate]: [STEPS.recommendations],
      [STEPS.review]: [STEPS.recommendations],
      [STEPS.cancellationReview]: [STEPS.recommendations],
      [STEPS.rescheduleConfirmation]: [],
      [STEPS.cancellationConfirmation]: [],
    };
    return [...(backMap[urlStep] || []), urlStep];
  };

  const [currentStep, setCurrentStep] = useState<string | null>(null);
  const reschedulingHistoryRef = useRef<string[]>(getInitialHistory());
  const [reschedulingHistory, setReschedulingHistory] = useState<string[]>(getInitialHistory());
  const [newAppointmentTime, setNewAppointmentTime] = useState(null);
  const { rescheduleOptions, rescheduleOptionsLoading, error, isError } =
    useGetRescheduleOptions(appointmentId);
  const {
    callInfo,
    clinicianAvailableAppointmentTimes,
    intersectionAvailability,
    clientMakeupDays,
    blockedReason,
  } = rescheduleOptions || {};

  React.useEffect(() => {
    if (rescheduleOptionsLoading) return;
    if (isError) {
      const is400 = error?.response?.status === 400;
      setStep(is400 ? STEPS.appointmentRemoved : STEPS.somethingWentWrong);
      return;
    }

    const scheduledTime = new Date(callInfo?.scheduled_date).getTime();
    const now = new Date().getTime();
    const isWithin24Hours = scheduledTime - now < 24 * 60 * 60 * 1000;
    const isWithin12Hours = scheduledTime - now < 12 * 60 * 60 * 1000;
    const isWithinRescheduleWindow = rescheduling12hrWindowPilot ? !isWithin12Hours : !isWithin24Hours;

    if (!isWithinRescheduleWindow) {
      setStep(STEPS.rescheduleWindowClosed);
      return;
    }

    const hasAvailableAppointments = intersectionAvailability?.length > 0 || clientMakeupDays?.length > 0;
    if (!hasAvailableAppointments) {
      // ANOW-6441: distinguish a supervisory-clinician conflict from no availability at all.
      setStep(blockedReason === "SUPERVISOR_UNAVAILABLE" ? STEPS.supervisorUnavailable : STEPS.noAppointments);
      return;
    }

    if (clientMakeupDays?.length > 0) setStep(STEPS.makeupDay);
    else setStep(STEPS.recommendations);
  }, [
    blockedReason,
    callInfo?.scheduled_date,
    clientMakeupDays?.length,
    error,
    intersectionAvailability?.length,
    isError,
    rescheduleOptions,
    rescheduleOptionsLoading,
    rescheduling12hrWindowPilot,
  ]);

  const setStep = (step: string) => {
    setCurrentStep(step);
    const newHistory = [...reschedulingHistoryRef.current, step];
    reschedulingHistoryRef.current = newHistory;
    setReschedulingHistory(newHistory);
    history.replace(`/rescheduling-session/${appointmentId}/${step}${searchParams}`);
  };

  const onBack = () => {
    const hist = reschedulingHistoryRef.current;
    const previousStep = hist[hist.length - 2];
    if (!previousStep) return;
    const newHistory = hist.slice(0, -1);
    reschedulingHistoryRef.current = newHistory;
    setReschedulingHistory(newHistory);
    setCurrentStep(previousStep);
    history.replace(`/rescheduling-session/${appointmentId}/${previousStep}${searchParams}`);
  };

  const contextValue = React.useMemo(
    () => ({
      currentStep,
      setStep,
      onBack,
      rescheduleOptionsLoading,
      callInfo,
      clinicianAvailableAppointmentTimes,
      intersectionAvailability,
      reschedulingHistory,
      newAppointmentTime,
      setNewAppointmentTime,
      clientMakeupDays,
      rescheduling12hrWindowPilot,
    }),
    [
      currentStep,
      reschedulingHistory,
      rescheduleOptionsLoading,
      callInfo,
      clinicianAvailableAppointmentTimes,
      intersectionAvailability,
      newAppointmentTime,
      clientMakeupDays,
      rescheduling12hrWindowPilot,
    ]
  );

  return (
    <ReschedulingContext.Provider value={contextValue}>{children}</ReschedulingContext.Provider>
  );
};

export const useRescheduling = () => {
  const context = React.useContext(ReschedulingContext);
  if (!context) {
    throw new Error("useRescheduling must be used within a ReschedulingProvider");
  }
  return context;
};
